'use client';

import type { ChangeEvent } from 'react';
import { useCallback } from 'react';
import { VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { Textarea } from '../../../../../components/styled/textarea';
import { Field } from '../../../../../components/field';
import {
  Select,
  createSelectListCollection,
} from '../../../../../components/select';
import type { MemoryData, MemoryCategory } from './types';
import { MEMORY_CATEGORY_LABELS, MEMORY_CATEGORY_DESCRIPTIONS } from './types';

const CATEGORY_OPTIONS: MemoryCategory[] = [
  'csrf_protection',
  'authentication_flow',
  'user_identity_mapping',
  'session_cookies',
  'profile_verification_pages',
  'not_found_behavior',
  'unauthenticated_access_patterns',
];

const categoryCollection = createSelectListCollection({
  items: CATEGORY_OPTIONS.map((category) => ({
    label: MEMORY_CATEGORY_LABELS[category],
    value: category,
  })),
});

export type MemoryEditFormProps = {
  memory: MemoryData;
  onChange: (memory: MemoryData) => void;
};

export function MemoryEditForm({ memory, onChange }: MemoryEditFormProps) {
  const t = useTranslations('designSystem.chat.editMemory');

  const handleContentChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      onChange({ ...memory, content: e.target.value });
    },
    [memory, onChange],
  );

  const handleCategoryChange = useCallback(
    (details: { value: string[] }) => {
      const value = details.value[0] as MemoryCategory;

      onChange({ ...memory, category: value });
    },
    [memory, onChange],
  );

  return (
    <VStack gap="4" alignItems="stretch">
      <Field.Root>
        <Field.Label>{t('contentLabel')}</Field.Label>
        <Textarea
          value={memory.content}
          onChange={handleContentChange}
          placeholder="Enter the memory content..."
          rows={5}
        />
        <Field.HelperText>{t('contentHelper')}</Field.HelperText>
      </Field.Root>

      <Field.Root>
        <Field.Label>{t('categoryLabel')}</Field.Label>
        <Select.Root
          collection={categoryCollection}
          value={memory.category ? [memory.category] : []}
          onValueChange={handleCategoryChange}
        >
          <Select.Control>
            <Select.Trigger>
              <Select.ValueText placeholder="Select a category" />
            </Select.Trigger>
          </Select.Control>
          <Select.Positioner>
            <Select.Content>
              {CATEGORY_OPTIONS.map((cat) => (
                <Select.Item key={cat} item={cat}>
                  <Select.ItemText>
                    {MEMORY_CATEGORY_LABELS[cat]}
                  </Select.ItemText>
                  <Select.ItemIndicator />
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Positioner>
        </Select.Root>
        <Field.HelperText>
          {memory.category
            ? MEMORY_CATEGORY_DESCRIPTIONS[memory.category]
            : t('categoryHelper')}
        </Field.HelperText>
      </Field.Root>
    </VStack>
  );
}
